import { useState } from 'react';
import { Bug } from 'lucide-react';
import { toast } from 'sonner';

export const TALLY_FORM_ID = 'GxXv12';
export const TALLY_EMBED_SRC = 'https://tally.so/widgets/embed.js';
export const TALLY_UNAVAILABLE_MESSAGE =
  'The bug report form could not be loaded. Check your connection or ad blocker and try again.';

interface TallyApi {
  openPopup: (
    formId: string,
    options?: { layout?: 'modal' | 'default'; width?: number; autoClose?: number },
  ) => void;
}

declare global {
  interface Window {
    Tally?: TallyApi;
  }
}

let tallyLoader: Promise<TallyApi> | null = null;

/**
 * Load the Tally widget on demand. Loading it eagerly from index.html logged a
 * blocked-request console error on every page for users behind ad blockers or
 * offline networks; now the script is only fetched when someone actually asks
 * to report a bug, and a failed load is reported as a toast instead.
 */
export function loadTally(): Promise<TallyApi> {
  if (window.Tally) return Promise.resolve(window.Tally);
  if (tallyLoader) return tallyLoader;

  tallyLoader = new Promise<TallyApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TALLY_EMBED_SRC;
    script.async = true;
    script.onload = () => {
      if (window.Tally) {
        resolve(window.Tally);
      } else {
        script.remove();
        reject(new Error('Tally script loaded but window.Tally is missing'));
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('Failed to load Tally embed script'));
    };
    document.head.appendChild(script);
  });
  tallyLoader.catch(() => {
    tallyLoader = null;
  });
  return tallyLoader;
}

/** Test hook: forget a previous (failed or succeeded) load so the next click retries. */
export function resetTallyLoaderForTests(): void {
  tallyLoader = null;
}

export function FeedbackButton() {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const tally = await loadTally();
      tally.openPopup(TALLY_FORM_ID, { layout: 'modal', width: 500, autoClose: 3000 });
    } catch {
      toast.error(TALLY_UNAVAILABLE_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      aria-busy={loading}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground hover:bg-gray-50 dark:hover:bg-accent rounded-lg transition-colors w-full disabled:opacity-60"
    >
      <Bug className="h-4 w-4 flex-shrink-0" />
      Report a Bug
    </button>
  );
}
